import classNames from 'classnames';
import { FC, ReactNode } from 'react';

import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { IconGallery } from 'src/general/Icons/IconGallery';
import { IconImperfections } from 'src/general/Icons/IconImperfections';
import { IconInterior } from 'src/general/Icons/IconInterior';
import { IconThumbsUp } from 'src/general/Icons/IconThumbsUp';
import { Image } from 'src/general/components/Image/Image';
import { getMicrocopy } from 'src/stores/microcopyStore';

import { PdpDivider } from '../PdpDivider/PdpDivider';
import styles from './CarGallery.module.scss';

interface GalleryItem {
  imageSrc: string;
  onClick: () => void;
}

interface Props {
  gallery: GalleryItem;
  imperfection?: GalleryItem;
  interior: GalleryItem;
}

export const CarGallery: FC<Props> = ({ gallery, imperfection, interior }) => {
  const noImperfections = getMicrocopy(MICROCOPY.PDP_GALLERY_NO_IMPERFECTIONS, true);
  const noImperfectionsMobile = getMicrocopy(MICROCOPY.PDP_GALLERY_NO_IMPERFECTIONS_MOBILE, true);

  return (
    <section className={styles.root}>
      <h3 className={styles.header}>{getMicrocopy(MICROCOPY.PDP_GALLERY_TITLE)}</h3>
      <div className={classNames(styles.content, { [styles.noImperfections]: !imperfection })}>
        <GalleryItemTrigger
          {...gallery}
          label={MICROCOPY.PDP_GALLERY_BUTTON_GALLERY}
          icon={<IconGallery />}
          className={styles.triggerGallery}
          priority
        />
        {!imperfection ? (
          <div className={styles.noImperfection}>
            <IconThumbsUp />
            <span className={styles.mdUp}>{noImperfections}</span>
            <span className={styles.mdDown}>{noImperfectionsMobile}</span>
          </div>
        ) : (
          <GalleryItemTrigger
            {...imperfection}
            label={MICROCOPY.PDP_GALLERY_BUTTON_IMPERFECTION}
            icon={<IconImperfections />}
            className={styles.triggerImperfection}
            priority
          />
        )}
        <GalleryItemTrigger
          {...interior}
          label={MICROCOPY.PDP_GALLERY_BUTTON_INTERIOR}
          icon={<IconInterior />}
          className={styles.triggerInterior}
          isInterior
        />
      </div>
      <PdpDivider className={styles.divider} />
    </section>
  );
};

const GalleryItemTrigger: FC<
  GalleryItem & { label: MICROCOPY; icon: ReactNode; className?: string; priority?: boolean; isInterior?: boolean }
> = ({ imageSrc, onClick, label, icon, className, priority, isInterior }) => {
  const labelValue = getMicrocopy(label);
  return (
    <button
      type="button"
      aria-label={`View ${labelValue}`}
      className={classNames(styles.item, className)}
      onClick={onClick}
      data-testid={label}
    >
      <Image
        src={imageSrc}
        className={styles.itemImage}
        alt=""
        width={660}
        height={330}
        priority={priority}
        sizes={isInterior ? undefined : '30vw'}
      />
      <span className={styles.itemDescription}>
        <span className={styles.itemIcon} aria-hidden="true">
          {icon}
        </span>
        {labelValue}
      </span>
    </button>
  );
};
